import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInsightsStore, PostInsights } from '../store/insights';
import { PageHeader } from '../components/PageHeader';

// Trend icons
const TrendIcon = ({ trend }: { trend: 'up' | 'down' | 'stable' }) => {
  if (trend === 'up') {
    return (
      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    );
  }
  if (trend === 'down') {
    return (
      <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
    </svg>
  );
};

// Stat Card Component
function StatCard({
  label,
  value,
  subtext,
  icon,
  color = 'blue',
}: {
  label: string;
  value: string | number;
  subtext?: string;
  icon: React.ReactNode;
  color?: 'blue' | 'green' | 'purple' | 'amber' | 'rose';
}) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>{icon}</div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
          {subtext && <p className="text-xs text-gray-400 mt-0.5">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

// Post Row Component for Table
function PostRow({ post, rank, getMediaTypeLabel }: { post: PostInsights; rank: number; getMediaTypeLabel: (type: string) => string }) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const truncateCaption = (caption: string | null, maxLength = 60) => {
    if (!caption) return '-';
    return caption.length > maxLength ? caption.slice(0, maxLength) + '...' : caption;
  };

  // Engagement rate color coding
  const getEngagementColor = (rate: number) => {
    if (rate >= 10) return 'text-green-600 bg-green-50';
    if (rate >= 5) return 'text-blue-600 bg-blue-50';
    if (rate >= 2) return 'text-amber-600 bg-amber-50';
    return 'text-gray-600 bg-gray-50';
  };

  // Quality score color coding
  const getQualityColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-gray-500';
  };

  return (
    <tr className="hover:bg-gray-50 border-b border-gray-100">
      <td className="px-3 py-3 text-center">
        <span className="text-sm font-medium text-gray-500">#{rank}</span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          {post.thumbnailUrl ? (
            <a href={post.permalink} target="_blank" rel="noopener noreferrer">
              <img
                src={post.thumbnailUrl}
                alt=""
                className="w-12 h-12 rounded object-cover hover:opacity-80 transition-opacity"
              />
            </a>
          ) : (
            <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm text-gray-900 truncate max-w-[200px]" title={post.caption || ''}>
              {truncateCaption(post.caption)}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                {getMediaTypeLabel(post.mediaType)}
              </span>
              <span className="text-xs text-gray-400">{formatDate(post.timestamp)}</span>
              <span className="text-xs text-gray-400">({post.ageInDays}d)</span>
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm font-medium text-gray-900">{post.reach.toLocaleString('de-DE')}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-gray-700">{post.likeCount}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-gray-700">{post.commentsCount}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-gray-700">{post.saved}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className="text-sm text-gray-700">{post.shares}</span>
      </td>
      <td className="px-3 py-3 text-right">
        <span className={`text-sm font-semibold px-2 py-1 rounded ${getEngagementColor(post.engagementRate)}`}>
          {post.engagementRate.toFixed(2)}%
        </span>
      </td>
      <td className="px-3 py-3 text-center">
        <span className={`text-lg font-bold ${getQualityColor(post.qualityScore)}`}>
          {post.qualityScore}
        </span>
      </td>
      <td className="px-3 py-3 text-center">
        <a
          href={post.permalink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </td>
    </tr>
  );
}

// Type Performance Card
function TypePerformanceCard({
  type,
  data,
  t,
  getMediaTypeLabel,
}: {
  type: string;
  data: { count: number; avgEngagementRate: number; avgReach: number; totalReach: number };
  t: (key: string) => string;
  getMediaTypeLabel: (type: string) => string;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900">{getMediaTypeLabel(type)}</h4>
        <span className="text-sm text-gray-500">{data.count} Posts</span>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">{t('analytics:avgEngagementRateLabel')}</span>
          <span className="font-medium text-gray-900">{data.avgEngagementRate.toFixed(2)}%</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">{t('analytics:avgReach')}</span>
          <span className="font-medium text-gray-900">{data.avgReach.toLocaleString('de-DE')}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">{t('analytics:totalReachLabel')}</span>
          <span className="font-medium text-gray-900">{data.totalReach.toLocaleString('de-DE')}</span>
        </div>
      </div>
    </div>
  );
}

// Main Analytics Page
export default function Analytics() {
  const { t } = useTranslation(['analytics', 'common']);

  // Dynamic media type labels
  const getMediaTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      IMAGE: t('analytics:mediaTypes.image'),
      VIDEO: t('analytics:mediaTypes.video'),
      CAROUSEL_ALBUM: t('analytics:mediaTypes.carousel'),
    };
    return labels[type] || type;
  };

  const {
    posts,
    summary,
    account,
    fetchedAt,
    isLoading,
    error,
    sortBy,
    sortOrder,
    filterType,
    fetchInsights,
    setSortBy,
    setSortOrder,
    setFilterType,
    getSortedPosts,
  } = useInsightsStore();

  const [activeTab, setActiveTab] = useState<'overview' | 'early' | 'posts'>('overview');

  // Early stage sorting (separate from main posts table)
  const [earlySortBy, setEarlySortBy] = useState<'ageInHours' | 'reach' | 'reachVelocity' | 'earlyPerformanceScore'>('ageInHours');
  const [earlySortOrder, setEarlySortOrder] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchInsights(50);
  }, [fetchInsights]);

  const sortedPosts = getSortedPosts();

  // Sort early stage posts
  const earlyPosts = posts
    .filter(p => p.ageInDays < 21)
    .sort((a, b) => {
      let aVal: number;
      let bVal: number;

      switch (earlySortBy) {
        case 'ageInHours':
          aVal = a.ageInHours;
          bVal = b.ageInHours;
          break;
        case 'reach':
          aVal = a.reach;
          bVal = b.reach;
          break;
        case 'reachVelocity':
          aVal = a.reachVelocity;
          bVal = b.reachVelocity;
          break;
        case 'earlyPerformanceScore':
          aVal = a.earlyPerformanceScore;
          bVal = b.earlyPerformanceScore;
          break;
        default:
          aVal = a.ageInHours;
          bVal = b.ageInHours;
      }

      return earlySortOrder === 'desc' ? bVal - aVal : aVal - bVal;
    });

  const handleEarlySort = (column: typeof earlySortBy) => {
    if (earlySortBy === column) {
      setEarlySortOrder(earlySortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setEarlySortBy(column);
      // Default sort orders per column
      if (column === 'ageInHours') {
        setEarlySortOrder('asc'); // Newest first
      } else {
        setEarlySortOrder('desc'); // Highest first
      }
    }
  };

  const EarlySortIndicator = ({ column }: { column: typeof earlySortBy }) => {
    if (earlySortBy !== column) return null;
    return (
      <svg className="w-4 h-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {earlySortOrder === 'desc' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        )}
      </svg>
    );
  };

  const handleSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const SortIndicator = ({ column }: { column: typeof sortBy }) => {
    if (sortBy !== column) return null;
    return (
      <svg className="w-4 h-4 inline-block ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {sortOrder === 'desc' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        )}
      </svg>
    );
  };

  // Get trend text
  const getTrendText = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up': return t('analytics:trend.rising');
      case 'down': return t('analytics:trend.falling');
      default: return t('analytics:trend.stable');
    }
  };

  // Get category label
  const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      exceptional: t('analytics:earlyStage.categories.exceptional'),
      above_average: t('analytics:earlyStage.categories.above_average'),
      average: t('analytics:earlyStage.categories.average'),
      below_average: t('analytics:earlyStage.categories.below_average'),
      new: t('analytics:earlyStage.categories.new'),
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <PageHeader
          icon={
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
          title={t('analytics:pageTitle')}
          subtitle={account ? `@${account.username} • ${account.followersCount} ${t('analytics:metrics.followers')}` : t('analytics:loadingShort')}
          breadcrumbs={[
            { label: t('analytics:breadcrumbs.marketing'), href: '/marketing' },
            { label: t('analytics:breadcrumbs.analytics') },
          ]}
          actions={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'overview'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {t('analytics:overview.tab')}
              </button>
              <button
                onClick={() => setActiveTab('early')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'early'
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {t('analytics:earlyStage.tab')}
              </button>
              <button
                onClick={() => setActiveTab('posts')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === 'posts'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {t('analytics:allPosts.tab')}
              </button>
              <Link
                to="/marketing/analytics/trends"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-purple-700 hover:bg-purple-50 border border-purple-300 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                {t('analytics:trends.title')}
              </Link>
              <Link
                to="/marketing/analytics/compare"
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors bg-white text-teal-700 hover:bg-teal-50 border border-teal-300 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                {t('analytics:compare.title')}
              </Link>
            </div>
          }
        />

        {/* Error State */}
        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p className="font-medium">{t('analytics:error.loadFailed')}</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="mt-6 flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Content */}
        {!isLoading && summary && (
          <>
            {activeTab === 'overview' && (
              <div className="mt-6 space-y-6">
                {/* KPI Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <StatCard
                    label={t('analytics:metrics.postsAnalyzed')}
                    value={summary.totalPosts}
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    }
                    color="blue"
                  />
                  <StatCard
                    label={t('analytics:metrics.totalReach')}
                    value={summary.totalReach.toLocaleString('de-DE')}
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    }
                    color="purple"
                  />
                  <StatCard
                    label={t('analytics:metrics.avgEngagementRate')}
                    value={`${summary.averageEngagementRate.toFixed(2)}%`}
                    subtext={getTrendText(summary.recentTrend)}
                    icon={<TrendIcon trend={summary.recentTrend} />}
                    color={summary.recentTrend === 'up' ? 'green' : summary.recentTrend === 'down' ? 'rose' : 'amber'}
                  />
                  <StatCard
                    label={t('analytics:metrics.saved')}
                    value={summary.totalSaves}
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    }
                    color="amber"
                  />
                  <StatCard
                    label={t('analytics:metrics.shared')}
                    value={summary.totalShares}
                    icon={
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    }
                    color="rose"
                  />
                </div>

                {/* Performance by Type */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics:performanceByType')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(summary.byType).map(([type, data]) => (
                      <TypePerformanceCard key={type} type={type} data={data} t={t} getMediaTypeLabel={getMediaTypeLabel} />
                    ))}
                  </div>
                </div>

                {/* Top Performers */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Top by Engagement Rate */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-green-500">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </span>
                      {t('analytics:topEngagementRate')}
                    </h4>
                    <div className="space-y-2">
                      {summary.topByEngagementRate.slice(0, 5).map((post, idx) => (
                        <a
                          key={post.id}
                          href={post.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-400 w-5">#{idx + 1}</span>
                          {post.thumbnailUrl && (
                            <img src={post.thumbnailUrl} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">{post.caption || t('analytics:noText')}</p>
                            <p className="text-xs text-gray-500">{getMediaTypeLabel(post.mediaType)}</p>
                          </div>
                          <span className="text-sm font-semibold text-green-600">{post.engagementRate.toFixed(1)}%</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Top by Reach */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-purple-500">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </span>
                      {t('analytics:topReach')}
                    </h4>
                    <div className="space-y-2">
                      {summary.topByReach.slice(0, 5).map((post, idx) => (
                        <a
                          key={post.id}
                          href={post.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-400 w-5">#{idx + 1}</span>
                          {post.thumbnailUrl && (
                            <img src={post.thumbnailUrl} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">{post.caption || t('analytics:noText')}</p>
                            <p className="text-xs text-gray-500">{getMediaTypeLabel(post.mediaType)}</p>
                          </div>
                          <span className="text-sm font-semibold text-purple-600">{post.reach.toLocaleString('de-DE')}</span>
                        </a>
                      ))}
                    </div>
                  </div>

                  {/* Top by Saves */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <span className="text-amber-500">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </span>
                      {t('analytics:topSaved')}
                    </h4>
                    <div className="space-y-2">
                      {summary.topBySaves.slice(0, 5).map((post, idx) => (
                        <a
                          key={post.id}
                          href={post.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm font-medium text-gray-400 w-5">#{idx + 1}</span>
                          {post.thumbnailUrl && (
                            <img src={post.thumbnailUrl} alt="" className="w-10 h-10 rounded object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 truncate">{post.caption || t('analytics:noText')}</p>
                            <p className="text-xs text-gray-500">{getMediaTypeLabel(post.mediaType)}</p>
                          </div>
                          <span className="text-sm font-semibold text-amber-600">{post.saved}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Footer Info */}
                {fetchedAt && (
                  <p className="text-sm text-gray-400 text-center">
                    {t('analytics:dataFrom', { date: new Date(fetchedAt).toLocaleString('de-DE') })}
                    <button
                      onClick={() => fetchInsights(50)}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      {t('common:refresh')}
                    </button>
                  </p>
                )}
              </div>
            )}

            {activeTab === 'early' && (
              <div className="mt-6 space-y-6">
                {/* Early Stage Header */}
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
                  <h2 className="text-xl font-bold mb-2">{t('analytics:earlyStage.title')}</h2>
                  <p className="text-green-100">
                    {t('analytics:earlyStage.description')}
                  </p>
                </div>

                {/* Benchmarks Card */}
                {summary.benchmarks && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics:earlyStage.benchmarks')}</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {t('analytics:earlyStage.benchmarksDescription')}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-2xl font-bold text-gray-900">
                          {summary.benchmarks.avgReachFirst24h.toFixed(0)}
                        </p>
                        <p className="text-sm text-gray-500">{t('analytics:earlyStage.reachAfter24h')}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-2xl font-bold text-gray-900">
                          {summary.benchmarks.avgReachFirst48h.toFixed(0)}
                        </p>
                        <p className="text-sm text-gray-500">{t('analytics:earlyStage.reachAfter48h')}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-2xl font-bold text-gray-900">
                          {summary.benchmarks.avgReachVelocity.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-500">{t('analytics:earlyStage.reachPerHour')}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-2xl font-bold text-gray-900">
                          {summary.benchmarks.avgEngagementVelocity.toFixed(3)}
                        </p>
                        <p className="text-sm text-gray-500">{t('analytics:earlyStage.engagementPerHour')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Potential Breakouts */}
                {summary.potentialBreakouts && summary.potentialBreakouts.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border-2 border-green-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-green-500">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </span>
                      <h3 className="text-lg font-semibold text-gray-900">{t('analytics:earlyStage.potentialBreakouts')}</h3>
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                        {t('analytics:earlyStage.lessThan3DaysOld')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">
                      {t('analytics:earlyStage.newPostsPerformingWell')}
                    </p>
                    <div className="space-y-3">
                      {summary.potentialBreakouts.map((post) => (
                        <a
                          key={post.id}
                          href={post.permalink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-4 p-4 rounded-lg bg-green-50 hover:bg-green-100 transition-colors"
                        >
                          {post.thumbnailUrl && (
                            <img src={post.thumbnailUrl} alt="" className="w-16 h-16 rounded-lg object-cover" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{post.caption || t('analytics:noText')}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-xs text-gray-500">{post.ageInHours}h alt</span>
                              <span className="text-xs text-gray-500">{post.reach} {t('analytics:metrics.reachShort')}</span>
                              <span className="text-xs text-gray-500">{post.reachVelocity.toFixed(1)} {t('analytics:metrics.reachShort')}/h</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-green-600">{post.earlyPerformanceScore}</div>
                            <div className="text-xs text-green-600">{t('analytics:earlyStage.score')}</div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Early Stage Performers Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('analytics:earlyStage.postsLast21Days')}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('analytics:earlyStage.post')}</th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                            <div className="group relative inline-flex items-center cursor-help">
                              <button onClick={() => handleEarlySort('ageInHours')} className="hover:text-gray-700">
                                {t('analytics:earlyStage.age')} <EarlySortIndicator column="ageInHours" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:earlyStage.ageTooltip')}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              <button onClick={() => handleEarlySort('reach')} className="hover:text-gray-700">
                                {t('analytics:metrics.reachShort')} <EarlySortIndicator column="reach" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-52 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:earlyStage.reachTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              <button onClick={() => handleEarlySort('reachVelocity')} className="hover:text-gray-700">
                                {t('analytics:earlyStage.velocity')} <EarlySortIndicator column="reachVelocity" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-56 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:earlyStage.velocityTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                            <div className="group relative inline-flex items-center cursor-help">
                              <button onClick={() => handleEarlySort('earlyPerformanceScore')} className="hover:text-gray-700">
                                {t('analytics:earlyStage.score')} <EarlySortIndicator column="earlyPerformanceScore" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:earlyStage.scoreTooltip')}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                            <div className="group relative inline-flex items-center cursor-help">
                              {t('analytics:earlyStage.status')}
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-52 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:earlyStage.statusTooltip')}
                                <div className="absolute bottom-full right-2 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {earlyPosts.map((post) => {
                            const categoryColors: Record<string, string> = {
                              exceptional: 'bg-green-100 text-green-800',
                              above_average: 'bg-blue-100 text-blue-800',
                              average: 'bg-gray-100 text-gray-800',
                              below_average: 'bg-amber-100 text-amber-800',
                              new: 'bg-purple-100 text-purple-800',
                            };
                            // Calculate velocity ratio vs benchmark
                            const benchmarkVelocity = summary.benchmarks?.avgReachVelocity || 1;
                            const velocityRatio = benchmarkVelocity > 0 ? post.reachVelocity / benchmarkVelocity : 1;
                            const velocityPercentage = ((velocityRatio - 1) * 100).toFixed(0);
                            const isAboveBenchmark = velocityRatio >= 1;

                            return (
                              <tr key={post.id} className="hover:bg-gray-50">
                                <td className="px-3 py-3">
                                  <div className="flex items-center gap-3">
                                    {post.thumbnailUrl && (
                                      <img src={post.thumbnailUrl} alt="" className="w-10 h-10 rounded object-cover" />
                                    )}
                                    <div className="min-w-0">
                                      <p className="text-sm text-gray-900 truncate max-w-[200px]">
                                        {post.caption?.slice(0, 50) || t('analytics:noText')}
                                      </p>
                                      <p className="text-xs text-gray-500">{getMediaTypeLabel(post.mediaType)}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-center text-sm text-gray-600">
                                  {post.ageInHours < 24
                                    ? `${post.ageInHours}h`
                                    : `${post.ageInDays}d`}
                                </td>
                                <td className="px-3 py-3 text-right">
                                  <div>
                                    <span className="text-sm font-medium text-gray-900">{post.reach.toLocaleString('de-DE')}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-right">
                                  <div className="flex flex-col items-end">
                                    <span className="text-sm font-medium text-gray-900">{post.reachVelocity.toFixed(2)}/h</span>
                                    <span className={`text-xs ${isAboveBenchmark ? 'text-green-600' : 'text-amber-600'}`}>
                                      {isAboveBenchmark ? '+' : ''}{velocityPercentage}% {t('analytics:earlyStage.vsAvg')}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 py-3">
                                  <div className="flex flex-col items-center">
                                    <span className={`text-lg font-bold ${
                                      post.earlyPerformanceScore >= 75 ? 'text-green-600' :
                                      post.earlyPerformanceScore >= 55 ? 'text-blue-600' :
                                      post.earlyPerformanceScore >= 40 ? 'text-gray-600' :
                                      'text-amber-600'
                                    }`}>
                                      {post.earlyPerformanceScore}
                                    </span>
                                    <span className="text-[10px] text-gray-400">
                                      {velocityRatio.toFixed(1)}x {t('analytics:earlyStage.benchmark')}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 py-3 text-center">
                                  <span className={`px-2 py-1 text-xs rounded-full ${categoryColors[post.performanceCategory]}`}>
                                    {getCategoryLabel(post.performanceCategory)}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Explanation Card */}
                <div className="bg-blue-50 rounded-xl p-6">
                  <h4 className="font-semibold text-blue-900 mb-2">{t('analytics:earlyStage.howScoreWorks')}</h4>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li><strong>{t('analytics:earlyStage.velocity')}</strong> = {t('analytics:earlyStage.scoreExplanation.velocity')}</li>
                    <li><strong>Score 75-100:</strong> {t('analytics:earlyStage.scoreExplanation.score75to100')}</li>
                    <li><strong>Score 55-74:</strong> {t('analytics:earlyStage.scoreExplanation.score55to74')}</li>
                    <li><strong>Score 40-54:</strong> {t('analytics:earlyStage.scoreExplanation.score40to54')}</li>
                    <li><strong>Score 0-39:</strong> {t('analytics:earlyStage.scoreExplanation.score0to39')}</li>
                    <li><strong>{t('analytics:earlyStage.categories.new')}:</strong> {t('analytics:earlyStage.scoreExplanation.tooNew')}</li>
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'posts' && (
              <div className="mt-6">
                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">{t('analytics:allPosts.typeFilter')}</label>
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value as typeof filterType)}
                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="all">{t('analytics:allPosts.typeAll')}</option>
                        <option value="IMAGE">{t('analytics:mediaTypes.images')}</option>
                        <option value="VIDEO">{t('analytics:mediaTypes.videosReels')}</option>
                        <option value="CAROUSEL_ALBUM">{t('analytics:mediaTypes.carousels')}</option>
                      </select>
                    </div>
                    <div className="text-sm text-gray-500">
                      {t('analytics:allPosts.postsOfTotal', { filtered: sortedPosts.length, total: posts.length })}
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                            #
                          </th>
                          <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <button onClick={() => handleSort('timestamp')} className="hover:text-gray-700">
                              Post <SortIndicator column="timestamp" />
                            </button>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              <button onClick={() => handleSort('reach')} className="hover:text-gray-700">
                                {t('analytics:metrics.reachShort')} <SortIndicator column="reach" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-52 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.reachTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              <button onClick={() => handleSort('likeCount')} className="hover:text-gray-700">
                                {t('analytics:metrics.likes')} <SortIndicator column="likeCount" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-48 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.likesTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              {t('analytics:metrics.commentsShort')}
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-48 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.commentsTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              <button onClick={() => handleSort('saved')} className="hover:text-gray-700">
                                {t('analytics:metrics.saves')} <SortIndicator column="saved" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-52 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.savesTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              {t('analytics:metrics.shares')}
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-52 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.sharesTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-end">
                              <button onClick={() => handleSort('engagementRate')} className="hover:text-gray-700">
                                {t('analytics:metrics.engagementRateShort')} <SortIndicator column="engagementRate" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-64 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.engagementRateTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <div className="group relative inline-flex items-center cursor-help justify-center">
                              <button onClick={() => handleSort('qualityScore')} className="hover:text-gray-700">
                                {t('analytics:metrics.qualityShort')} <SortIndicator column="qualityScore" />
                              </button>
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-72 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.qualityScoreTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                          <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                            <div className="group relative inline-flex items-center cursor-help justify-center">
                              {t('analytics:allPosts.link')}
                              <svg className="w-3.5 h-3.5 ml-1 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="absolute top-full right-0 mt-2 w-40 px-3 py-2 text-xs text-left text-white bg-gray-900 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 font-normal normal-case shadow-lg">
                                {t('analytics:allPosts.linkTooltip')}
                                <div className="absolute bottom-full right-4 border-4 border-transparent border-b-gray-900"></div>
                              </div>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {sortedPosts.map((post, idx) => (
                          <PostRow key={post.id} post={post} rank={idx + 1} getMediaTypeLabel={getMediaTypeLabel} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{t('analytics:allPosts.legend')}</h4>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-green-50 text-green-600">10%+</span>
                      {t('analytics:allPosts.excellent')}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-600">5-10%</span>
                      {t('analytics:allPosts.good')}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-600">2-5%</span>
                      {t('analytics:allPosts.average')}
                    </span>
                    <span className="flex items-center gap-2">
                      <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-600">&lt;2%</span>
                      {t('analytics:allPosts.low')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('analytics:allPosts.engagementRateFormula')}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
